import * as React from "react";
import { Link } from "react-router-dom";
import Sidebar from "../Sidebar";
import "./Navbar.css";
import { TbMenu2, TbChevronDown } from "react-icons/tb";
import brandStripe from "../../Images/brand-stripe.png";
import frlLogo from "../../Images/FRL Logo.webp";
import projectsData from "../../Projects/projectsData";

// Home is reached via the logo, so it is intentionally absent here.
// "Projects" links to the index page and also opens a dropdown of individual
// projects on hover/focus.
const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects", dropdown: true },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/ourteam", label: "Our Team" },
  { to: "/join-us", label: "Join Us" },
];

export default function Navbar() {
  const [hidden, setHidden] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  // The projects panel is state-driven rather than :hover/:focus-within so that
  // clicking a link can close it. Navigation doesn't unmount the navbar, so a
  // CSS-only panel stays open: the clicked anchor keeps focus and holds
  // :focus-within true until something else is clicked.
  const [projectsOpen, setProjectsOpen] = React.useState(false);
  const lastY = React.useRef(0);

  // Only close when focus genuinely leaves the item — moving between the
  // trigger and the links inside the panel must not collapse it. A null
  // relatedTarget means focus dropped to the body, which does count as leaving.
  const handleDropdownBlur = (event) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    setProjectsOpen(false);
  };

  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY.current && y > 80) {
        setHidden(true);
      } else if (y < lastY.current) {
        setHidden(false);
      }
      lastY.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <nav
        className={hidden ? "Navbar NavbarHidden" : "Navbar"}
        aria-label="Main navigation"
      >
        <div className="NavbarContainer">
          <span className="NavbarLeftAccent" aria-hidden="true" />

          <Link className="NavbarLogo" to="/" aria-label="Florida Rocket Lab home">
            <img
              className="NavbarBrandStripe"
              src={brandStripe}
              width="1024"
              height="442"
              alt=""
            />
          </Link>

          <ul className="NavbarMenu">
            {NAV_LINKS.map((item) => (
              <li
                key={item.label}
                className={
                  item.dropdown
                    ? `NavbarItem NavbarItemDropdown${projectsOpen ? " NavbarItemOpen" : ""}`
                    : "NavbarItem"
                }
                onMouseEnter={item.dropdown ? () => setProjectsOpen(true) : undefined}
                onMouseLeave={item.dropdown ? () => setProjectsOpen(false) : undefined}
                onFocus={item.dropdown ? () => setProjectsOpen(true) : undefined}
                onBlur={item.dropdown ? handleDropdownBlur : undefined}
              >
                {item.dropdown ? (
                  <Link
                    className="NavbarLink NavbarDropdownTrigger"
                    to={item.to}
                    aria-haspopup="true"
                    aria-expanded={projectsOpen}
                    onClick={() => setProjectsOpen(false)}
                  >
                    {item.label}
                    <TbChevronDown className="NavbarChevron" aria-hidden="true" />
                  </Link>
                ) : (
                  <Link className="NavbarLink" to={item.to}>{item.label}</Link>
                )}
                {item.dropdown && (
                  <div className="NavbarDropdown">
                    <Link
                      className="NavbarDropdownLink NavbarDropdownLead"
                      to="/projects"
                      onClick={() => setProjectsOpen(false)}
                    >
                      Projects Overview
                    </Link>
                    {projectsData.map((project) => (
                      <Link
                        key={project.slug}
                        className="NavbarDropdownLink"
                        to={`/projects/${project.slug}`}
                        onClick={() => setProjectsOpen(false)}
                      >
                        {project.title}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <span className="NavbarRightAccent" aria-hidden="true" />

          <Link className="NavbarBrandRight" to="/" aria-label="Florida Rocket Lab home">
            <img className="NavbarBrandRightLogo" src={frlLogo} alt="" />
          </Link>

          <button
            type="button"
            className="NavbarMobileIcon"
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            <TbMenu2 aria-hidden="true" />
          </button>
        </div>
      </nav>
    </>
  );
}
